import { Trans, t } from '@lingui/macro'
import { useMemo } from 'react'
import { Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@mui/material'
import { AIUsageStatistics } from '@/core/api/statistics'
import { ChartSkeleton } from './ChartSkeleton'
import { STATISTICS_CONSTANTS } from '../lib/constants'

type AIUsageByUserComponentProps = {
  data: AIUsageStatistics | undefined
  isLoading: boolean
}

export function AIUsageByUserComponent({ data, isLoading }: AIUsageByUserComponentProps) {
  const topUsers = useMemo(() => {
    if (!data) return []

    const userMap = new Map<
      string,
      {
        userId: string
        name: string
        surname: string
        email: string
        transcriptions: number
        reports: number
        total: number
      }
    >()

    data.transcriptions.byUser.forEach((user) => {
      const existing = userMap.get(user.userId) || {
        userId: user.userId,
        name: user.name,
        surname: user.surname,
        email: user.email,
        transcriptions: 0,
        reports: 0,
        total: 0,
      }
      existing.transcriptions = user.count
      existing.total += user.count
      userMap.set(user.userId, existing)
    })

    data.reports.byUser.forEach((user) => {
      const existing = userMap.get(user.userId) || {
        userId: user.userId,
        name: user.name,
        surname: user.surname,
        email: user.email,
        transcriptions: 0,
        reports: 0,
        total: 0,
      }
      existing.reports = user.count
      existing.total += user.count
      userMap.set(user.userId, existing)
    })

    return Array.from(userMap.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, STATISTICS_CONSTANTS.TOP_USERS_LIMIT)
  }, [data])

  if (isLoading) {
    return <ChartSkeleton />
  }

  if (topUsers.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          <Trans>No top users found</Trans>
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        <Trans>Top users by AI usage</Trans>
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Trans>User</Trans>
              </TableCell>
              <TableCell align="right">{t`Transcriptions`}</TableCell>
              <TableCell align="right">{t`Reports`}</TableCell>
              <TableCell align="right">
                <Trans>Total</Trans>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topUsers.map((user, index) => (
              <TableRow key={user.userId} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {user.name} {user.surname}
                      </Typography>
                      {index < 3 && (
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          color={index === 0 ? 'primary' : index === 1 ? 'secondary' : 'default'}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Chip label={user.transcriptions} color="primary" variant="outlined" size="small" />
                </TableCell>
                <TableCell align="right">
                  <Chip label={user.reports} color="secondary" variant="outlined" size="small" />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {user.total}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
